import { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { getCloseReadiness, markPeriodClosed } from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { ArrowLeftRight, AlertTriangle, FileText, ShieldCheck, Flag, Lock, ChevronLeft, ChevronRight, CheckCircle2 } from 'lucide-react';

const STATUS_META = {
  not_ready: { color: '#C84B31', bg: 'rgba(157,2,8,0.08)', label: 'Not Ready' },
  in_progress: { color: '#D98A4A', bg: 'rgba(244,162,97,0.1)', label: 'In Progress' },
  ready_for_review: { color: '#D98A4A', bg: 'rgba(198,156,109,0.1)', label: 'Ready for Review' },
  ready_for_cpa: { color: '#2D7A5D', bg: 'rgba(45,106,79,0.1)', label: 'Ready for CPA' },
  closed: { color: '#1A1A1A', bg: 'rgba(28,28,30,0.06)', label: 'Closed' },
};

function ScoreRing({ score, size = 120 }) {
  const radius = (size - 12) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const color = score >= 85 ? '#2D7A5D' : score >= 70 ? '#D98A4A' : score >= 30 ? '#D98A4A' : '#C84B31';
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#E5E3DB" strokeWidth="8" />
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={color} strokeWidth="8" strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={offset} className="transition-all duration-1000" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-heading font-medium" style={{ color }}>{Math.round(score)}</span>
        <span className="text-[9px] font-body uppercase tracking-wider" style={{ color: '#9CA3AF' }}>Score</span>
      </div>
    </div>
  );
}

export default function CloseReadinessPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getCloseReadiness(period);
      setData(res.data);
    } catch { toast.error('Failed to load close readiness'); }
    setLoading(false);
  }, [period]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const navigatePeriod = (dir) => {
    const [y, m] = period.split('-').map(Number);
    const d = new Date(y, m - 1 + dir, 1);
    setPeriod(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  const handleClose = async () => {
    try {
      await markPeriodClosed({ period });
      toast.success(`Period ${period} marked as closed`);
      fetchData();
    } catch { toast.error('Failed to close period'); }
  };

  const periodLabel = (() => {
    const [y, m] = period.split('-').map(Number);
    return new Date(y, m - 1).toLocaleString('en-US', { month: 'long', year: 'numeric' });
  })();

  if (loading) return <Layout title="Close Readiness"><div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#D98A4A', borderTopColor: 'transparent' }}></div></div></Layout>;

  const bd = data?.breakdown || {};
  const statusMeta = STATUS_META[data?.status] || STATUS_META.not_ready;

  const cards = [
    { key: 'reconciliations', label: 'Reconciliations', icon: ArrowLeftRight, detail: `${bd.reconciliations?.completed || 0} of ${bd.reconciliations?.total || 0} complete` },
    { key: 'exceptions', label: 'Open Exceptions', icon: AlertTriangle, detail: `${bd.exceptions?.pending || 0} pending, ${bd.exceptions?.sensitive_pending || 0} sensitive` },
    { key: 'documents', label: 'Missing Documents', icon: FileText, detail: `${bd.documents?.missing || 0} of ${bd.documents?.total_material || 0} need docs` },
    { key: 'sensitive_items', label: 'Sensitive Items', icon: ShieldCheck, detail: `${bd.sensitive_items?.reviewed || 0} of ${bd.sensitive_items?.total || 0} reviewed` },
    { key: 'policy_flags', label: 'Policy Flags', icon: Flag, detail: `${bd.policy_flags?.pending || 0} flags pending` },
  ];

  return (
    <Layout title="Close Readiness">
      <div data-testid="close-readiness-page" className="space-y-6">
        {/* Period Navigator + Status */}
        <div className="bg-white rounded-xl p-6" style={{ border: '1px solid #E5E3DB' }}>
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <button onClick={() => navigatePeriod(-1)} className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#F7F6F2' }} data-testid="period-prev"><ChevronLeft size={16} /></button>
              <h2 className="font-heading text-2xl font-medium" style={{ color: '#1A1A1A' }} data-testid="period-label">{periodLabel}</h2>
              <button onClick={() => navigatePeriod(1)} className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#F7F6F2' }} data-testid="period-next"><ChevronRight size={16} /></button>
            </div>
            <div className="flex items-center gap-3">
              <span className="px-4 py-1.5 rounded-full text-xs font-body font-semibold" style={{ background: statusMeta.bg, color: statusMeta.color }} data-testid="close-status-badge">
                {statusMeta.label}
              </span>
              {data?.status === 'ready_for_cpa' && (
                <Button onClick={handleClose} className="rounded-full font-body text-sm text-white" style={{ background: '#1A1A1A' }} data-testid="mark-closed-btn">
                  <Lock size={14} className="mr-2" /> Mark as Closed
                </Button>
              )}
            </div>
          </div>

          {/* Score Ring + Breakdown */}
          <div className="flex items-center gap-10">
            <ScoreRing score={data?.composite_score || 0} size={140} />
            <div className="flex-1 space-y-3">
              {cards.map(({ key, label, icon: Icon, detail }) => {
                const score = bd[key]?.score || 0;
                const barColor = score >= 85 ? '#2D7A5D' : score >= 70 ? '#D98A4A' : score >= 30 ? '#D98A4A' : '#C84B31';
                return (
                  <div key={key} data-testid={`readiness-${key}`}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <Icon size={14} style={{ color: barColor }} />
                        <span className="text-sm font-body font-medium" style={{ color: '#1A1A1A' }}>{label}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs font-body" style={{ color: '#4B5563' }}>{detail}</span>
                        <span className="text-xs font-mono font-semibold" style={{ color: barColor }}>{Math.round(score)}%</span>
                      </div>
                    </div>
                    <div className="h-1.5 rounded-full overflow-hidden" style={{ background: '#E5E3DB' }}>
                      <div className="h-full rounded-full transition-all duration-700" style={{ width: `${score}%`, background: barColor }}></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Status Steps */}
        <div className="bg-white rounded-xl p-6" style={{ border: '1px solid #E5E3DB' }}>
          <h3 className="text-[10px] font-body uppercase tracking-[0.2em] font-semibold mb-4" style={{ color: '#9CA3AF' }}>Close Progress</h3>
          <div className="flex items-center justify-between">
            {Object.entries(STATUS_META).map(([key, meta], i) => {
              const isActive = key === data?.status;
              const isPast = Object.keys(STATUS_META).indexOf(key) < Object.keys(STATUS_META).indexOf(data?.status);
              return (
                <div key={key} className="flex items-center flex-1">
                  <div className="flex flex-col items-center flex-1">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center mb-2 transition-all" style={{
                      background: isPast ? '#2D7A5D' : isActive ? meta.color : '#E5E3DB',
                    }}>
                      {isPast ? <CheckCircle2 size={16} className="text-white" /> : <span className="text-xs font-mono font-bold" style={{ color: isActive ? '#fff' : '#9CA3AF' }}>{i + 1}</span>}
                    </div>
                    <span className="text-[10px] font-body font-medium text-center" style={{ color: isActive ? meta.color : '#9CA3AF' }}>{meta.label}</span>
                  </div>
                  {i < 4 && <div className="h-0.5 flex-1 mx-1 -mt-5" style={{ background: isPast ? '#2D7A5D' : '#E5E3DB' }}></div>}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </Layout>
  );
}
